// Top navigation — adapted from Inku's actual production dashboard.
// Pattern: rounded Inku-blue logo (left), simplified nav (Home / Assets /
// Brand), centered search bar, theme toggle + notifications + avatar
// (right). Same framer-motion shared-layout active indicator.

import { motion } from "framer-motion";
import {
  Bell,
  Moon,
  Palette,
  Search,
  Sparkles,
  Sun,
} from "lucide-react";
import { useEffect, useState } from "react";

import { useStore } from "@/lib/store";
import type { Route } from "@/lib/types";
import { cn } from "@/lib/utils";

import { UserMenu } from "./UserMenu";

interface NavItem {
  name: string;
  href: string;
  matchRoutes?: Route["name"][];
}

const navItems: NavItem[] = [
  { name: "Home", href: "/", matchRoutes: ["home"] },
  { name: "Assets", href: "/canvas", matchRoutes: ["canvas", "t"] },
  { name: "Brand", href: "/brand", matchRoutes: ["brand"] },
  { name: "Brand graph", href: "/graph", matchRoutes: ["graph"] },
];

export function MainNav() {
  const { route, navigate, setToast } = useStore();
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [search, setSearch] = useState("");

  // Track theme via document class (Inku globals.css conventions).
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, [theme]);

  const isActive = (item: NavItem): boolean =>
    item.matchRoutes?.includes(route.name) ?? false;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur-md supports-[backdrop-filter]:bg-card/82">
      <div className="mx-auto max-w-[1600px] h-14 flex items-center px-4 md:px-6 gap-4">
        {/* Logo — Inku-blue gradient rounded square, no workspace name */}
        <button
          onClick={() => navigate("/")}
          className="shrink-0 outline-none rounded-xl"
        >
          <InkuLogo />
        </button>

        {/* Nav */}
        <nav className="flex items-center gap-1 shrink-0">
          {navItems.map((item) => {
            const active = isActive(item);
            return (
              <button
                key={item.name}
                onClick={() => navigate(item.href)}
                className="relative px-3 py-2 rounded-lg outline-none"
              >
                {active && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute inset-0 bg-accent rounded-lg"
                    transition={{
                      type: "spring",
                      bounce: 0.15,
                      duration: 0.5,
                    }}
                  />
                )}
                <span
                  className={cn(
                    "relative text-sm font-medium",
                    active
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground transition-colors"
                  )}
                >
                  {item.name}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Search — flex-1 takes remaining space, capped at max */}
        <div className="flex-1 flex justify-center max-w-2xl mx-auto">
          <div className="relative w-full max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Try 'brand campaign'"
              className={cn(
                "w-full pl-10 pr-4 py-2 rounded-full bg-muted/60 hover:bg-muted",
                "focus:bg-background focus:ring-2 focus:ring-ring/30 focus:bg-card",
                "border border-transparent focus:border-border",
                "outline-none text-sm placeholder:text-muted-foreground/60",
                "transition-all"
              )}
            />
          </div>
        </div>

        {/* Right: theme toggle, bell, user */}
        <div className="flex items-center gap-1 shrink-0">
          <IconButton
            label={theme === "dark" ? "Switch to light" : "Switch to dark"}
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </IconButton>
          <IconButton
            label="Notifications"
            onClick={() =>
              setToast({
                kind: "ok",
                text: "No new notifications.",
              })
            }
          >
            <Bell className="h-4 w-4" />
          </IconButton>
          <div className="ml-1.5">
            <UserMenu />
          </div>
        </div>
      </div>
    </header>
  );
}

function InkuLogo() {
  return (
    <div
      className="h-9 w-9 rounded-xl flex items-center justify-center shadow-sm"
      style={{
        background:
          "linear-gradient(135deg, hsl(252 98% 49%) 0%, hsl(252 82% 65%) 100%)",
      }}
    >
      <span className="font-freight text-white text-xl font-medium leading-none">
        ı
      </span>
    </div>
  );
}

function IconButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={cn(
        "h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground",
        "hover:bg-accent hover:text-foreground transition-colors outline-none",
        "focus-visible:ring-2 focus-visible:ring-ring/50"
      )}
    >
      {children}
    </button>
  );
}

// Re-exported for legacy callers; some pages still import these.
export { Sparkles, Palette };
